import type { ITableBaseCellContext } from '../../components/ITableBaseCellContext';
import { type TPandasDataFrameDict } from '../../components/TPandasDataFrameDict';
import { type TPandasDataFrameValue } from '../../components/TPandasDataFrameValue';

export class HTableAlterations {
  static appendRow(
    table: TPandasDataFrameDict,
    values?: TPandasDataFrameValue[],
    callback?: (modifiedTable: TPandasDataFrameDict) => void
  ): TPandasDataFrameDict {
    let modifiedTable = JSON.parse(JSON.stringify(table));
    const columns = Object.keys(modifiedTable);
    const newRowName = self.crypto.randomUUID();
    for (const column of columns) {
      modifiedTable[column][newRowName] = null;
    }
    if (values) {
      modifiedTable = HTableAlterations.replaceRow(modifiedTable, newRowName, values);
    }
    callback?.(modifiedTable);
    return modifiedTable;
  }

  static replaceCell(
    originalTable: TPandasDataFrameDict,
    context: Omit<ITableBaseCellContext, 'rowValues'>,
    callback?: (modifiedTable: TPandasDataFrameDict) => void
  ): TPandasDataFrameDict {
    const columns = Object.keys(originalTable);
    const modifiedTable = JSON.parse(JSON.stringify(originalTable));
    modifiedTable[columns[context.cellCoordinate[0]]][context.rowName] = context.value;
    callback?.(modifiedTable);
    return modifiedTable;
  }

  static deleteColumns(
    originalTable: TPandasDataFrameDict,
    columns: string[],
    callback?: (modifiedTable: TPandasDataFrameDict) => void
  ): TPandasDataFrameDict {
    const cols = Object.keys(originalTable);
    const modifiedTable: TPandasDataFrameDict = {};
    for (const column of cols) {
      if (!columns.includes(column)) {
        modifiedTable[column] = originalTable[column];
      }
    }
    callback?.(modifiedTable);
    return modifiedTable;
  }

  static deleteRows(
    originalTable: TPandasDataFrameDict,
    rows: string[],
    callback?: (modifiedTable: TPandasDataFrameDict) => void
  ): TPandasDataFrameDict {
    const columns = Object.keys(originalTable);
    const rowNames = Array.from(new Set(columns.flatMap((col) => Object.keys(originalTable[col]))).values());
    const modifiedTable: TPandasDataFrameDict = {};
    for (const col of columns) {
      modifiedTable[col] = {};
      for (const row of rowNames) {
        if (!rows.includes(row)) {
          modifiedTable[col][row] = originalTable[col][row];
        }
      }
    }
    callback?.(modifiedTable);
    return modifiedTable;
  }

  static extractRow(table: TPandasDataFrameDict, rowName: string): TPandasDataFrameValue[] {
    const columns = Object.keys(table);
    const row: TPandasDataFrameValue[] = [];
    for (const column of columns) {
      const val = table[column][rowName];
      row.push(val);
    }
    return row;
  }

  static replaceRow(
    table: TPandasDataFrameDict,
    rowName: string,
    values: TPandasDataFrameValue[],
    callback?: (modifiedTable: TPandasDataFrameDict) => void
  ): TPandasDataFrameDict {
    const columns = Object.keys(table);
    const modifiedTable = JSON.parse(JSON.stringify(table));
    for (const column of columns) {
      const targetValue = values[columns.indexOf(column)];
      modifiedTable[column][rowName] =
        targetValue === undefined ? modifiedTable[column][rowName] : (targetValue ?? null);
    }
    callback?.(modifiedTable);
    return modifiedTable;
  }

  static recordsToColumns(
    records: Array<Record<string, TPandasDataFrameValue>>,
    indexColumn?: string
  ): TPandasDataFrameDict {
    const result: Record<string, Record<string, TPandasDataFrameValue>> = {};

    records.forEach((record, rowIndex) => {
      const index = indexColumn ? String(record[indexColumn]) : String(rowIndex);

      Object.entries(record).forEach(([column, value]) => {
        if (column === indexColumn) return;

        result[column] ??= {};
        result[column][index] = value;
      });
    });

    return result;
  }
}
